import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";

// API imports
import useAxios from "../../../hooks/useAxios";
import jsonFormat from "../../../apis/jsonFormat";

// Redux
import { useDispatch } from "react-redux";
import { setUserData } from "../../../redux/features/userState/userStateSlice";
import {
  setActiveMenu,
  setActivePage,
  setAppOpen,
} from "../../../redux/features/applicationState/applicationStateSlice";

// Custom components
import toastManager from "../../../helpers/toastManager";
import AuthenticationCard from "../../../components/Authentication/AuthenticationCard";
import {
  RegularField,
  PasswordField,
} from "../../../components/InputFields/InputFields";

// Detect enter press
import { useEnterKeyPress } from "../../../hooks/useEnterKeyPress";

const LoginScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // API Start
  const { response, error, axiosFetch } = useAxios();
  const handleSubmit = () => {
    // Verify no fields are empty.
    if (username && password) {
      axiosFetch({
        axiosInstance: jsonFormat(),
        method: "POST",
        url: "user/login",
        sendSignal: false,
        requestConfig: {
          username: username,
          password: password,
          authentication_origin: "clinical_dashboard",
        },
      });
    } else {
      toastManager(
        "warning",
        "Cannot log in.",
        "Please fill out all fields properly."
      );
    }
    !username ? setUsernameWarning(true) : setUsernameWarning(false);
    !password ? setPasswordWarning(true) : setPasswordWarning(false);
  };

  // RESPONSE Detection
  useEffect(() => {
    // Function to check if any property of the user object is null or undefined
    const hasNullOrUndefinedProperty = (obj) =>
      Object.values(obj).some((value) => value === null || value === undefined);

    // 1: Custom action code handling.
    // User is successfully authenticated, and a token is received.
    if (response?.action_code === "login_success") {
      if (response?.payload) {
        const user = {
          access: response.payload.access,
          email: response.payload.email,
          id: response.payload.id,
          username: response.payload.username,
        };

        if (hasNullOrUndefinedProperty(user)) {
          toastManager(
            "error",
            "Issue Logging in!",
            "Payload missing some values."
          );
        } else {
          navigate("/");

          dispatch(setUserData(user));
          dispatch(setAppOpen(true));
          dispatch(setActiveMenu("home"));
          dispatch(setActivePage("/"));

          toastManager("success", "Logging in!", "Welcome to Dermi.");
        }
      } else {
        toastManager("error", "Issue Logging in!", "Response missing payload.");
      }
    }
  }, [response]);

  // ERROR Detection
  useEffect(() => {
    if (error) {
      // 1: Custom action_code handling.
      // Username and password doesn't match to user.
      if (error?.action_code === "authentication_failed") {
        toastManager("error", "Unauthorized!", "Invalid username or password.");
      }
    }
  }, [error]);
  // API End

  // Detect enter press
  useEnterKeyPress(handleSubmit);

  const [username, setUsername] = useState("");
  const [usernameWarning, setUsernameWarning] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordWarning, setPasswordWarning] = useState(false);
  return (
    <AuthenticationCard
      AuthenticationPromptTitle={"Login"}
      AuthenticationPromptText={"Enter your username and password to log in."}
      AuthenticationButtonPrompt={"Login"}
      AuthenticationButtonFunction={handleSubmit}
      redirectOption={"register"}
    >
      <RegularField
        fieldLabel="Username"
        placeholderText="Enter username"
        value={username}
        fieldWarning={usernameWarning}
        customOnChange={(event) => {
          // Prevent spaces
          let newUsername = event.target.value.replace(/\s/g, "");
          setUsername(newUsername);
        }}
      />

      <PasswordField
        passwordLabel={"Password"}
        placeholderText={"Enter your password"}
        setFunction={setPassword}
        value={password}
        passwordWarning={passwordWarning}
      />

      <div className="flex flex-grow items-center justify-end w-full sm:w-4/5 mt-1 mb-2">
        <Link
          to={"/email_reset_request"}
          className=" font-medium text-sm text-neutral-400 hover:text-neutral-600 cursor-pointer"
        >
          Forgot password or username?
        </Link>
      </div>
    </AuthenticationCard>
  );
};

export default LoginScreen;
